import { useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { upgradeUserAccount } from '../../utils/users'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import { GuardrailModal } from '../common/modals/GuardrailModal'
import { TextArea } from '../common/fields/TextArea'
import { WorkflowPage } from '../workflows/WorkflowPage'

const render = ({ base }: PocketComponentProps<void>) => {
    const [user, setUser] = useState(base.me.clone())
    const [modalState, setModalState] = useState<'upgrade_modal' | undefined>(undefined)
    const navigate = usePSNavigate()
    const [isSaving, setIsSaving] = useState(false)

    const hasEdits =
        user.orderPolicy !== base.me.orderPolicy ||
        user.bookingPolicy !== base.me.bookingPolicy ||
        user.invoicePolicy !== base.me.invoicePolicy ||
        user.receiptPolicy !== base.me.receiptPolicy ||
        user.estimatePolicy !== base.me.estimatePolicy

    function closeModal() {
        setModalState(undefined)
    }

    const submitChanges = async () => {
        if (user.solo || user.company || user.partner) {
            setIsSaving(true)
            const result = await RecordFactory.getInstance().sendUser(user)
            setIsSaving(false)

            if (!result.ok) {
                logAndDisplayError(result.error)
                return
            }
            navigate('/settings')
        }
        else {
            setModalState('upgrade_modal')
        }
    }

    function upgradeAccount() {
        upgradeUserAccount(base, user, usePSNavigate)
    }

    return (
        <WorkflowPage
            title={user.payee || user.client ? 'Upgrade to business' : 'Default memos'}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button
                        intent="primary"
                        disabled={!hasEdits}
                        onClick={submitChanges}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    <div className="flex flex-col justify-between space-y-8">
                        <WorkflowPage.IconSection
                            title="Invoice"
                            icon={'document2.large'}
                            description="Default invoice memos will prefill when sending invoices to clients. You can always adjust it on the fly if needed."
                        >
                            <TextArea
                                isAutoResizing
                                size="medium"
                                className="h-45"
                                placeholder="Add a default invoice memo"
                                value={user.invoicePolicy}
                                onChange={(event) =>
                                    setUser(user.clone({ invoice_policy: event.target.value }))
                                }
                                onClear={() => setUser(user.clone({ invoice_policy: '' }))}
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Charge"
                            icon={'dollar.large'}
                            description="Default charge memos will prefill when sending charges to
                                    clients. You can always adjust it on the fly if needed."
                        >
                            <TextArea
                                isAutoResizing
                                size="medium"
                                className="h-45"
                                placeholder="Add a default charge memo"
                                value={user.receiptPolicy}
                                onChange={(event) =>
                                    setUser(user.clone({ receipt_policy: event.target.value }))
                                }
                                onClear={() => setUser(user.clone({ receipt_policy: '' }))}
                            />
                        </WorkflowPage.IconSection>

                        <WorkflowPage.IconSection
                            title="Appointment"
                            icon={'calendar.large'}
                            description="Default appointment memos will prefill when sending appointments
                                        to clients. You can always adjust it on the fly if needed."
                        >
                            <TextArea
                                isAutoResizing
                                size="medium"
                                className="h-45"
                                placeholder="Add a default appointment memo"
                                value={user.bookingPolicy}
                                onChange={(event) =>
                                    setUser(user.clone({ booking_policy: event.target.value }))
                                }
                                onClear={() => setUser(user.clone({ booking_policy: '' }))}
                            />
                        </WorkflowPage.IconSection>

                        {user.allowEstimate && (
                            <WorkflowPage.IconSection
                                title="Estimate"
                                icon={'dollar4.large'}
                                description="Default estimate memos will prefill when sending estimates
                                        to clients. You can always adjust it on the fly if needed."
                            >
                                <TextArea
                                    isAutoResizing
                                    size="medium"
                                    className="h-45"
                                    placeholder="Add a default estimate memo"
                                    value={user.estimatePolicy}
                                    onChange={(event) =>
                                        setUser(user.clone({ estimate_policy: event.target.value }))
                                    }
                                    onClear={() => setUser(user.clone({ estimate_policy: '' }))}
                                />
                            </WorkflowPage.IconSection>
                        )}
                        {user.allowProduct && (
                            <WorkflowPage.IconSection
                                title="Product order"
                                icon={'package.large'}
                                description="Default product order memos will prefill when sending
                                            product orders to clients"
                            >
                                <TextArea
                                    isAutoResizing
                                    size="medium"
                                    className="h-45"
                                    placeholder="Add a default product order memo"
                                    value={user.orderPolicy}
                                    onChange={(event) =>
                                        setUser(user.clone({ order_policy: event.target.value }))
                                    }
                                    onClear={() => setUser(user.clone({ estimate_policy: '' }))}
                                />
                            </WorkflowPage.IconSection>
                        )}
                    </div>
                </WorkflowPage.Card>
            </div>
            {modalState === 'upgrade_modal' && (
                <GuardrailModal
                    title="Upgrade to business account"
                    buttonText="Upgrade to business account"
                    onClose={closeModal}
                    onAction={upgradeAccount}
                >
                    <Paragraph>Would you like to upgrade to a business account?</Paragraph>
                </GuardrailModal>
            )}

            <NavigationBlockerPrompt when={hasEdits && !isSaving} />
        </WorkflowPage>
    )
}

export const SetupDescriptionsPage = usePSComponent(render)
function logAndDisplayError(error: unknown) {
    throw new Error('Function not implemented.')
}
